import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { append, patch } from "@ngxs/store/operators";
import { IUser } from "@zonar-ui/auth/lib/models/user.model";
import { EMPTY } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { ErrorResponse } from "src/app/models/error-response";
import { Repair, RepairResolution } from "src/app/models/repair.model";
import { RepairService } from "../service/repair.service";

export class RepairStateModel {
	numberOfDefects: number;
	resolutions: RepairResolution[];
	repair: Array<Repair | ErrorResponse>;
	allMechanics: Array<IUser>;
}

export class SetAllMechanics {
	static readonly type = "[Repairs] Set All Mechanics";
	constructor(public mechanicsAndSupervisingMechanics: Array<IUser>) {}
}

export class GetCompanyResolutions {
	static readonly type = "[Repairs] Get Company Resolutions";
	constructor(public companyPayload: string, public endpoint: string) {}
}

export class PostRepair {
	static readonly type = "[Repairs] Post Repair On Defect(s)";
	constructor(public repairPayload: Repair, public defectPayload: string, public endpoint: string) {}
}

export class ResetRepairs {
	static readonly type = "[Repairs] Reset Repair to Empty Array";
}

export class SetNumberOfDefects {
	static readonly type = "[Repairs] Set Number of Defects";
	constructor(public defectLengthPayload: number) {}
}

@Injectable()
@State<RepairStateModel>({
	name: "repair",
})
export class RepairState {
	constructor(private repairService: RepairService) {}

	@Selector()
	static getAllMechanics(state: RepairStateModel) {
		return state.allMechanics;
	}

	@Selector()
	static getCompanyResolutions(state: RepairStateModel) {
		return state.resolutions;
	}

	@Selector()
	static getRepair(state: RepairStateModel) {
		return state.repair;
	}

	@Selector()
	static getNumberOfDefects(state: RepairStateModel) {
		return state.numberOfDefects;
	}

	@Action(SetAllMechanics)
	setAllMechanics(
		{ patchState }: StateContext<RepairStateModel>,
		{ mechanicsAndSupervisingMechanics }: SetAllMechanics,
	) {
		return patchState({
			allMechanics: mechanicsAndSupervisingMechanics,
		});
	}

	@Action(GetCompanyResolutions)
	getCompanyResolutions(
		{ patchState }: StateContext<RepairStateModel>,
		{ companyPayload, endpoint }: GetCompanyResolutions,
	) {
		return this.repairService.getResolution(companyPayload, endpoint).pipe(
			tap((result: RepairResolution[]) => {
				patchState({
					resolutions: result,
				});
			}),
		);
	}

	@Action(PostRepair)
	postRepair({ setState }: StateContext<RepairStateModel>, { repairPayload, defectPayload, endpoint }: PostRepair) {
		return this.repairService.postRepairStatus(repairPayload, defectPayload, endpoint).pipe(
			tap((result: HttpResponse<Repair>) => {
				setState(
					patch({
						repair: append<Repair | ErrorResponse>([result.body]),
					}),
				);
			}),
			catchError((error: HttpErrorResponse) => {
				// Handle the error here
				console.error("Could not post repair", error);
				setState(
					patch({
						repair: append<ErrorResponse | Repair>([new ErrorResponse(error.error)]),
					}),
				);
				return EMPTY;
			}),
		);
	}

	@Action(ResetRepairs)
	resetRepairs({ patchState }: StateContext<RepairStateModel>) {
		return patchState({
			repair: [],
		});
	}

	@Action(SetNumberOfDefects)
	setNumberOfDefects({ patchState }: StateContext<RepairStateModel>, { defectLengthPayload }: SetNumberOfDefects) {
		return patchState({
			numberOfDefects: defectLengthPayload,
		});
	}
}
